import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ShipmentsIcon from '@mui/icons-material/AirplanemodeActiveOutlined';
import MyDesignsIcon from '@mui/icons-material/DesignServicesOutlined';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import AccountSettingsIcon from '@mui/icons-material/ManageAccounts';
import MenuIcon from '@mui/icons-material/Menu';
import BillingIcon from '@mui/icons-material/ReceiptOutlined';
import ProfileSettingsIcon from '@mui/icons-material/SettingsOutlined';
import StoreIcon from '@mui/icons-material/Storefront';
import ManageAccountsIcon from '@mui/icons-material/SwitchAccountOutlined';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import type { StackProps } from '@mui/material/Stack';
import Stack from '@mui/material/Stack';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import SvgIcon from '@mui/material/SvgIcon';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { json } from '@remix-run/node';
import type { ShouldRevalidateFunction } from '@remix-run/react';
import { Outlet, useLoaderData, useLocation, useMatches, useRouteLoaderData } from '@remix-run/react';
import type { LoaderArgs } from '@remix-run/server-runtime';
import * as React from 'react';
import { Image } from 'remix-image';
import Cart, { cartWidth } from '~/components/Cart';
import Logo from '~/components/Logo';
import { UserAvatar } from '~/components/Navbar';
import AnnounceKitComponent from '~/components/Navbar/AnnounceKitComponent';
import { CartButton, Logout, MenuEntry } from '~/components/Navbar/common';
import useHeader from '~/components/hooks/useHeader';
import { MuiRemixLink, RemixLink } from '~/components/shared/links';
import type { LoaderData as RootLoaderData } from '~/root';
import type { Cart as TCart } from '~/services/api.server';
import { scApi } from '~/services/api.server';
import { requireAuthentication } from '~/services/auth.server';
import { getSession } from '~/services/session.server';
import { isErrorResponse } from '~/utils/common';
import { scrollbarStyle } from '~/utils/common-styles';

export type DashboardLoaderData = Awaited<ReturnType<Awaited<ReturnType<typeof loader>>['json']>>;

export const shouldRevalidate: ShouldRevalidateFunction = ({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
  formData,
}) => {
  const action = formData?.get('_action')?.toString();
  if (action !== 'selectAccount' && currentUrl.pathname === nextUrl.pathname) {
    return false;
  }
  return defaultShouldRevalidate;
};

export async function loader({ request }: LoaderArgs) {
  const { user } = await requireAuthentication(request);
  const session = await getSession(request);
  const accountId = session.get('accountId');
  const [me, inventory] = await Promise.all([
    scApi.getMe(user.accessToken, accountId),
    scApi.getInventoryItems(user.accessToken, accountId, { limit: '1' }),
  ]);

  return json({ user: me, hasInventory: !isErrorResponse(inventory) && inventory.count > 0 });
}

export const dashboardLinks = {
  dashboard: [
    {
      id: 0,
      icon: <HomeIcon width="20px" height="20px" />,
      text: 'Dashboard',
      to: '/dashboard',
    },
    {
      id: 1,
      icon: <MyDesignsIcon />,
      text: 'My products',
      to: '/dashboard/my-products',
    },
    {
      id: 7,
      icon: <StoreIcon />,
      text: 'Stores',
      to: '/dashboard/stores',
      flag: 'enable-web-stores',
    },
    {
      id: 2,
      icon: <OrdersIcon width="20px" height="20px" />,
      text: 'Orders',
      to: '/dashboard/orders',
    },
    {
      id: 3,
      icon: <InventoryIcon />,
      text: 'Inventory',
      to: '/dashboard/inventory',
    },
    {
      id: 4,
      icon: <RecipientsIcon />,
      text: 'Recipients',
      to: '/dashboard/recipients',
    },
    {
      id: 5,
      icon: <ShipmentsIcon />,
      text: 'Shipments',
      to: '/dashboard/shipments',
    },
    {
      id: 6,
      icon: <BillingIcon />,
      text: 'Invoices',
      to: '/dashboard/billing',
    },
  ],
  profile: [
    {
      id: 0,
      icon: <ManageAccountsIcon />,
      text: 'Manage Accounts',
      to: '/dashboard/manage-accounts',
    },
    {
      id: 1,
      icon: <AccountSettingsIcon />,
      text: 'Account Settings',
      to: '/dashboard/account-settings/details',
    },
    {
      id: 2,
      icon: <ProfileSettingsIcon />,
      text: 'Profile Settings',
      to: '/dashboard/profile',
    },
  ],
  logout: (showIcon: boolean) => ({
    name: 'Logout',
    component: <Logout showIcon={showIcon} />,
  }),
};

const drawerOpen = 275;
const drawerClose = 68;
const menuOptions = [
  ...dashboardLinks.profile.map((link) => ({
    name: link.text,
    component: (id?: number) => <MenuEntry entry={link.text} href={link.to} icon={link.icon} />,
  })),
  dashboardLinks.logout(true),
];

function UserAuthForm({ handleModalClose }: { handleModalClose?: () => void }) {
  const [userMenuAnchorEl, setUserMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const { user } = useLoaderData<typeof loader>();
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    handleModalClose?.();
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => setUserMenuAnchorEl(null);

  const account = user?.requested_account ?? user?.current_account;

  return (
    <Stack>
      {user?.profile_picture ? (
        <Image
          src={user.profile_picture}
          width={32}
          height={32}
          style={{ borderRadius: '50%', marginLeft: '24px', marginRight: '4px', cursor: 'pointer' }}
          alt="User Image"
          responsive={[{ size: { height: 32, width: 32 } }]}
          options={{ fit: 'cover' }}
          dprVariants={[1, 3]}
          loading="lazy"
          onClick={handleUserMenuOpen}
        />
      ) : (
        <IconButton
          onClick={handleUserMenuOpen}
          sx={{ width: 32, height: 32, marginLeft: '24px', marginRight: '4px', cursor: 'pointer' }}
        >
          <AccountCircleIcon color="disabled" sx={{ width: 32, height: 32 }} />
        </IconButton>
      )}

      <Menu
        anchorEl={userMenuAnchorEl}
        id="user-menu"
        keepMounted
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={Boolean(userMenuAnchorEl)}
        onClose={handleUserMenuClose}
        PaperProps={{ sx: { width: 273, top: '60px !important', '.MuiIconButton-root': { pl: 0 } } }}
      >
        {user && (
          <Stack direction="row" px={2}>
            <UserAvatar
              first_name={user.first_name}
              last_name={user.last_name}
              profile_picture={user.profile_picture}
              current_account={account}
            />
          </Stack>
        )}
        <Divider sx={{ my: 0.5, mx: 2, px: 2 }} />

        {menuOptions.map((option) => {
          if (option.name === 'Divider') {
            return <Divider key={option.name} sx={{ my: 0.5, px: 2 }} />;
          }
          if (typeof option.component === 'function') {
            return (
              <MenuItem key={option.name} onClick={handleUserMenuClose} sx={{ p: 0, py: 0.5 }}>
                {option.component(account?.id)}
              </MenuItem>
            );
          } else {
            return (
              <MenuItem key={option.name} onClick={handleUserMenuClose} sx={{ p: 0, py: 0.5 }}>
                {option.component}
              </MenuItem>
            );
          }
        })}
      </Menu>
    </Stack>
  );
}

type NavbarDashboardProps = {
  cart: TCart;
  drawerWidth: number;
  setMobileOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenCart: React.Dispatch<React.SetStateAction<boolean>>;
};
function NavbarDashboard({ cart, drawerWidth, setMobileOpen, setOpenCart }: NavbarDashboardProps) {
  const [hasScroll] = useHeader();
  const theme = useTheme();
  const { user, hasInventory } = useLoaderData<typeof loader>();

  const toolBar = (
    <Toolbar sx={{ pl: { xs: '16px !important', md: '40px !important' } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" py={2}>
        <IconButton
          color="inherit"
          onClick={() => setMobileOpen(true)}
          aria-label="show more"
          sx={{ display: { xs: 'flex', md: 'none' } }}
        >
          <MenuIcon sx={{ color: 'primary.dark' }} />
        </IconButton>
        <Box pr={3} display={{ xs: 'none', md: 'flex' }}>
          {user && (
            <Typography variant="h5" color="text.primary">
              Hi {user.first_name || user.username}!
            </Typography>
          )}
        </Box>
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
          <Tooltip title={hasInventory ? '' : 'There is nothing in your inventory to be sent'} placement="bottom" arrow>
            <span>
              <Button
                LinkComponent={RemixLink}
                href="/send-swag/from-inventory?redirectTo=/dashboard"
                variant="outlined"
                aria-label="send swag"
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                  ml: 2,
                }}
                disableElevation
                disabled={!hasInventory}
              >
                Send Swag
              </Button>
            </span>
          </Tooltip>

          <Button
            LinkComponent={RemixLink}
            href="/shop"
            variant="contained"
            aria-label="start new order"
            sx={{
              display: { xs: 'none', md: 'flex' },
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              ml: 2,
            }}
            disableElevation
          >
            Start new order
          </Button>
          <UserAuthForm />
          <AnnounceKitComponent />
          <CartButton totalProducts={cart.products.length} setOpenCart={setOpenCart} />
        </Stack>
      </Stack>
    </Toolbar>
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={4}
        sx={{
          display: { xs: 'none', md: 'flex' },
          pl: `${drawerWidth}px`,
          height: 'auto',
          bgcolor: '#FFF',
          boxShadow: hasScroll
            ? '-4px 2px 4px -1px rgb(0 0 0 / 20%), -4px 4px 5px 0px rgb(0 0 0 / 14%), -8px 1px 10px 0px rgb(0 0 0 / 12%)'
            : 'none',
          transition: theme.transitions.create('padding', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {toolBar}
      </AppBar>

      <AppBar
        position="sticky"
        elevation={4}
        sx={{
          display: { xs: 'flex', md: 'none' },
          pl: 0,
          height: 'auto',
          bgcolor: '#FFF',
          boxShadow: hasScroll
            ? '-4px 2px 4px -1px rgb(0 0 0 / 20%), -4px 4px 5px 0px rgb(0 0 0 / 14%), -8px 1px 10px 0px rgb(0 0 0 / 12%)'
            : 'none',
          transition: theme.transitions.create('padding', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {toolBar}
      </AppBar>
    </>
  );
}

export function HomeIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props}>
      <path d="M21 20.0003C21 20.2655 20.8946 20.5198 20.7071 20.7074C20.5196 20.8949 20.2652 21.0003 20 21.0003H4C3.73478 21.0003 3.48043 20.8949 3.29289 20.7074C3.10536 20.5198 3 20.2655 3 20.0003V9.49027C2.99989 9.33788 3.03462 9.18749 3.10152 9.05057C3.16841 8.91365 3.26572 8.79384 3.386 8.70027L11.386 2.47827C11.5615 2.34172 11.7776 2.26758 12 2.26758C12.2224 2.26758 12.4385 2.34172 12.614 2.47827L20.614 8.70027C20.7343 8.79384 20.8316 8.91365 20.8985 9.05057C20.9654 9.18749 21.0001 9.33788 21 9.49027V20.0003V20.0003ZM19 19.0003V9.97827L12 4.53427L5 9.97827V19.0003H19Z" />
    </SvgIcon>
  );
}

export function OrdersIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props}>
      <path d="M7.83002 20C7.66098 20.4779 7.37364 20.9052 6.99481 21.242C6.61598 21.5788 6.15801 21.8141 5.66363 21.9261C5.16924 22.038 4.65455 22.023 4.16758 21.8822C3.68061 21.7415 3.23722 21.4797 2.87879 21.1212C2.52036 20.7628 2.25855 20.3194 2.11781 19.8324C1.97707 19.3455 1.96197 18.8308 2.07393 18.3364C2.18588 17.842 2.42124 17.384 2.75805 17.0052C3.09486 16.6264 3.52214 16.339 4.00002 16.17V7.82999C3.52214 7.66095 3.09486 7.37361 2.75805 6.99478C2.42124 6.61595 2.18588 6.15798 2.07393 5.6636C1.96197 5.16921 1.97707 4.65452 2.11781 4.16755C2.25855 3.68058 2.52036 3.23719 2.87879 2.87876C3.23722 2.52032 3.68061 2.25852 4.16758 2.11778C4.65455 1.97704 5.16924 1.96194 5.66363 2.0739C6.15801 2.18585 6.61598 2.42121 6.99481 2.75802C7.37364 3.09483 7.66098 3.52211 7.83002 3.99999H16.17C16.3391 3.52211 16.6264 3.09483 17.0052 2.75802C17.3841 2.42121 17.842 2.18585 18.3364 2.0739C18.8308 1.96194 19.3455 1.97704 19.8325 2.11778C20.3194 2.25852 20.7628 2.52032 21.1213 2.87876C21.4797 3.23719 21.7415 3.68058 21.8822 4.16755C22.023 4.65452 22.0381 5.16921 21.9261 5.6636C21.8142 6.15798 21.5788 6.61595 21.242 6.99478C20.9052 7.37361 20.4779 7.66095 20 7.82999V16.17C20.4779 16.339 20.9052 16.6264 21.242 17.0052C21.5788 17.384 21.8142 17.842 21.9261 18.3364C22.0381 18.8308 22.023 19.3455 21.8822 19.8324C21.7415 20.3194 21.4797 20.7628 21.1213 21.1212C20.7628 21.4797 20.3194 21.7415 19.8325 21.8822C19.3455 22.023 18.8308 22.038 18.3364 21.9261C17.842 21.8141 17.3841 21.5788 17.0052 21.242C16.6264 20.9052 16.3391 20.4779 16.17 20H7.83002ZM7.83002 18H16.17C16.3196 17.578 16.5616 17.1947 16.8782 16.8781C17.1947 16.5615 17.578 16.3196 18 16.17V7.82999C17.578 7.68039 17.1947 7.43846 16.8782 7.12186C16.5616 6.80527 16.3196 6.42199 16.17 5.99999H7.83002C7.68042 6.42199 7.43849 6.80527 7.1219 7.12186C6.8053 7.43846 6.42202 7.68039 6.00002 7.82999V16.17C6.42202 16.3196 6.8053 16.5615 7.1219 16.8781C7.43849 17.1947 7.68042 17.578 7.83002 18ZM5.00002 5.99999C5.26524 5.99999 5.51959 5.89464 5.70713 5.7071C5.89467 5.51956 6.00002 5.26521 6.00002 4.99999C6.00002 4.73478 5.89467 4.48042 5.70713 4.29289C5.51959 4.10535 5.26524 3.99999 5.00002 3.99999C4.73481 3.99999 4.48045 4.10535 4.29292 4.29289C4.10538 4.48042 4.00002 4.73478 4.00002 4.99999C4.00002 5.26521 4.10538 5.51956 4.29292 5.7071C4.48045 5.89464 4.73481 5.99999 5.00002 5.99999ZM19 5.99999C19.2652 5.99999 19.5196 5.89464 19.7071 5.7071C19.8947 5.51956 20 5.26521 20 4.99999C20 4.73478 19.8947 4.48042 19.7071 4.29289C19.5196 4.10535 19.2652 3.99999 19 3.99999C18.7348 3.99999 18.4805 4.10535 18.2929 4.29289C18.1054 4.48042 18 4.73478 18 4.99999C18 5.26521 18.1054 5.51956 18.2929 5.7071C18.4805 5.89464 18.7348 5.99999 19 5.99999ZM19 20C19.2652 20 19.5196 19.8946 19.7071 19.7071C19.8947 19.5196 20 19.2652 20 19C20 18.7348 19.8947 18.4804 19.7071 18.2929C19.5196 18.1054 19.2652 18 19 18C18.7348 18 18.4805 18.1054 18.2929 18.2929C18.1054 18.4804 18 18.7348 18 19C18 19.2652 18.1054 19.5196 18.2929 19.7071C18.4805 19.8946 18.7348 20 19 20ZM5.00002 20C5.26524 20 5.51959 19.8946 5.70713 19.7071C5.89467 19.5196 6.00002 19.2652 6.00002 19C6.00002 18.7348 5.89467 18.4804 5.70713 18.2929C5.51959 18.1054 5.26524 18 5.00002 18C4.73481 18 4.48045 18.1054 4.29292 18.2929C4.10538 18.4804 4.00002 18.7348 4.00002 19C4.00002 19.2652 4.10538 19.5196 4.29292 19.7071C4.48045 19.8946 4.73481 20 5.00002 20Z" />
    </SvgIcon>
  );
}

export function InventoryIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props}>
      <path d="M21 11.646V21C21 21.2652 20.8946 21.5196 20.7071 21.7071C20.5196 21.8946 20.2652 22 20 22H4C3.73479 22 3.48043 21.8946 3.2929 21.7071C3.10536 21.5196 3 21.2652 3 21V11.646C2.35432 10.916 1.99856 9.97461 2 9V3C2 2.73478 2.10536 2.48043 2.2929 2.29289C2.48043 2.10536 2.73479 2 3 2H21C21.2652 2 21.5196 2.10536 21.7071 2.29289C21.8946 2.48043 22 2.73478 22 3V9C22 10.014 21.622 10.94 21 11.646ZM19 12.874C18.2849 13.0577 17.5328 13.0403 16.827 12.8236C16.1211 12.6069 15.4889 12.1994 15 11.646C14.625 12.0721 14.1635 12.4133 13.6461 12.6468C13.1288 12.8802 12.5676 13.0007 12 13C11.4324 13.0007 10.8712 12.8802 10.3539 12.6468C9.83655 12.4133 9.37498 12.0721 9 11.646C8.51232 12.2009 7.88013 12.6096 7.17389 12.8264C6.46764 13.0432 5.71507 13.0597 5 12.874V20H19V12.874ZM14 9C14 8.73478 14.1054 8.48043 14.2929 8.29289C14.4804 8.10536 14.7348 8 15 8C15.2652 8 15.5196 8.10536 15.7071 8.29289C15.8946 8.48043 16 8.73478 16 9C16 9.53043 16.2107 10.0391 16.5858 10.4142C16.9609 10.7893 17.4696 11 18 11C18.5304 11 19.0391 10.7893 19.4142 10.4142C19.7893 10.0391 20 9.53043 20 9V4H4V9C4 9.53043 4.21072 10.0391 4.58579 10.4142C4.96086 10.7893 5.46957 11 6 11C6.53044 11 7.03914 10.7893 7.41422 10.4142C7.78929 10.0391 8 9.53043 8 9C8 8.73478 8.10536 8.48043 8.2929 8.29289C8.48043 8.10536 8.73479 8 9 8C9.26522 8 9.51957 8.10536 9.70711 8.29289C9.89465 8.48043 10 8.73478 10 9C10 9.53043 10.2107 10.0391 10.5858 10.4142C10.9609 10.7893 11.4696 11 12 11C12.5304 11 13.0391 10.7893 13.4142 10.4142C13.7893 10.0391 14 9.53043 14 9Z" />
    </SvgIcon>
  );
}

export function RecipientsIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props}>
      <path d="M3 2H19.005C20.107 2 21 2.898 21 3.99V20.01C21 21.109 20.107 22 19.005 22H3V2ZM7 4H5V20H7V4ZM9 20H19V4H9V20ZM11 16C11 15.2044 11.3161 14.4413 11.8787 13.8787C12.4413 13.3161 13.2044 13 14 13C14.7956 13 15.5587 13.3161 16.1213 13.8787C16.6839 14.4413 17 15.2044 17 16H11ZM14 12C13.4696 12 12.9609 11.7893 12.5858 11.4142C12.2107 11.0391 12 10.5304 12 10C12 9.46957 12.2107 8.96086 12.5858 8.58579C12.9609 8.21071 13.4696 8 14 8C14.5304 8 15.0391 8.21071 15.4142 8.58579C15.7893 8.96086 16 9.46957 16 10C16 10.5304 15.7893 11.0391 15.4142 11.4142C15.0391 11.7893 14.5304 12 14 12ZM22 6H24V10H22V6ZM22 12H24V16H22V12Z" />
    </SvgIcon>
  );
}

function PermanentDrawer({
  drawerWidth,
  drawerMobileWidth,
  open,
  setOpen,
  mobileOpen,
  setMobileOpen,
  inMediumBreakpoint,
}: {
  drawerWidth: number;
  drawerMobileWidth: number;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  mobileOpen: boolean;
  setMobileOpen: React.Dispatch<React.SetStateAction<boolean>>;
  inMediumBreakpoint: boolean;
}) {
  const theme = useTheme();
  const { flags } = useRouteLoaderData('root') as RootLoaderData;
  const { user } = useLoaderData<typeof loader>();
  const handleMenuClickMobile = () => {
    if (!inMediumBreakpoint) {
      setMobileOpen(false);
    }
  };

  const account = user?.requested_account ?? user?.current_account;

  const drawer = (
    <Stack
      p="10px"
      width={drawerWidth}
      justifyContent="space-between"
      height="100%"
      overflow="hidden"
      sx={{
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      <IconButton
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'absolute',
          top: '59px',
          right: '-13px',
          p: 0.5,
          border: `1px solid ${theme.palette.primary.main}`,
          color: 'primary.main',
          bgcolor: '#fff',
          '&:hover': {
            bgcolor: '#fff',
          },
        }}
        onClick={inMediumBreakpoint ? () => setOpen(!open) : () => setMobileOpen(false)}
      >
        {mobileOpen ? <KeyboardDoubleArrowLeftIcon sx={{ width: 16, height: 16 }} /> : null}
      </IconButton>

      <IconButton
        sx={{
          display: { xs: 'none', md: 'flex' },
          position: 'absolute',
          top: '59px',
          right: '-13px',
          p: 0.5,
          border: `1px solid ${theme.palette.primary.main}`,
          color: 'primary.main',
          bgcolor: '#fff',
          '&:hover': {
            bgcolor: '#fff',
          },
        }}
        onClick={inMediumBreakpoint ? () => setOpen(!open) : () => setMobileOpen(false)}
      >
        {open ? (
          <KeyboardDoubleArrowLeftIcon sx={{ width: 16, height: 16 }} />
        ) : (
          <KeyboardDoubleArrowRightIcon sx={{ width: 16, height: 16 }} />
        )}
      </IconButton>

      <Stack
        flex={1}
        overflow="hidden"
        sx={{
          ' & > div > form': {
            pl: '12px !important',
            py: !open ? 0 : 0.75,
            height: !open ? 52 : 'inherit',
            color: 'text.secondary',

            borderRadius: 3,
            '&:hover': {
              color: 'primary.main',
              backgroundColor: 'rgba(0, 157, 200, 0.08)',
            },
            '& > button': {
              fontSize: 16,
              pl: 2,
              // used to prevent the form from moving on open
              height: 40,
            },
            '& > svg': {
              fill: 'text.secondary',
              fillOpacity: 0.6,
            },
          },
        }}
      >
        <Box height="43px">
          {!inMediumBreakpoint ? (
            <Logo variant="dark" isResponsive={false} />
          ) : open ? (
            <Logo variant="dark" isResponsive={true} />
          ) : (
            <Logo variant="dark" isSmall={true} />
          )}
        </Box>

        <Divider sx={{ mt: '19px', mb: 1.5 }} />

        <Stack flex={1} sx={{ overflowX: 'hidden', ...scrollbarStyle, maxHeight: 'calc(100dvh - 90px)' }}>
          <List sx={{ py: 0 }}>
            {dashboardLinks.dashboard
              .filter((link) => !link.flag || flags[link.flag])
              .map((option) => (
                <DashboardLink
                  inMediumBreakpoint={inMediumBreakpoint}
                  handleMenuClickMobile={handleMenuClickMobile}
                  key={option.id}
                  open={open}
                  option={option}
                />
              ))}
          </List>
          <Divider sx={{ my: 1.5 }} />
          <List sx={{ py: 0 }}>
            {dashboardLinks.profile.map((option) => (
              <DashboardLink
                inMediumBreakpoint={inMediumBreakpoint}
                handleMenuClickMobile={handleMenuClickMobile}
                key={option.id}
                open={open}
                option={option}
                id={account?.id}
              />
            ))}
          </List>
          <Logout showIcon />
        </Stack>
      </Stack>

      {/* <Stack>
        <Divider style={{ marginTop: '13px' }} />
        <List>
          <DashboardLink
            inMediumBreakpoint={inMediumBreakpoint}
            handleMenuClickMobile={handleMenuClickMobile}
            open={open}
            option={{
              icon: <HelpOutlineIcon width={20} height={20} />,
              text: 'Help & getting started',
              to: 'https://support.swagcharm.com/en/',
              id: 4,
              highlightIcon: true,
            }}
          />
        </List>
      </Stack> */}
    </Stack>
  );

  const drawerMobile = (
    <Stack
      p="16px 10px"
      width={drawerMobileWidth}
      justifyContent="space-between"
      height="100%"
      overflow="hidden"
      sx={{
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      <IconButton
        sx={{
          position: 'absolute',
          top: '59px',
          right: '-13px',
          p: 0.5,
          border: `1px solid ${theme.palette.primary.main}`,
          color: 'primary.main',
          bgcolor: '#fff',
          '&:hover': {
            bgcolor: '#fff',
          },
        }}
        onClick={inMediumBreakpoint ? () => setOpen(!open) : () => setMobileOpen(false)}
      >
        {inMediumBreakpoint ? (
          open ? (
            <KeyboardDoubleArrowLeftIcon sx={{ width: 16, height: 16 }} />
          ) : (
            <KeyboardDoubleArrowRightIcon sx={{ width: 16, height: 16 }} />
          )
        ) : null}

        {!inMediumBreakpoint && mobileOpen ? <KeyboardDoubleArrowLeftIcon sx={{ width: 16, height: 16 }} /> : null}
      </IconButton>

      <Stack
        sx={{
          ' & > div > form': {
            pl: '18px !important',
            py: 0.75,
            color: 'text.secondary',
            borderRadius: 3,
            '&:hover': {
              color: 'primary.main',
              backgroundColor: 'rgba(0, 157, 200, 0.08)',
            },
            '& > button': {
              fontSize: 16,
              pl: 2,
            },
            '& > svg': {
              fill: 'text.secondary',
              fillOpacity: 0.6,
            },
          },
        }}
      >
        <Box height="43px">
          {!inMediumBreakpoint ? (
            <Logo variant="dark" isResponsive={false} />
          ) : open ? (
            <Logo variant="dark" isResponsive={true} />
          ) : (
            <Logo variant="dark" isSmall={true} />
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />
        <Stack sx={{ overflowX: 'hidden', ...scrollbarStyle, maxHeight: 'calc(100dvh - 110px)' }}>
          <List sx={{ py: 0 }}>
            {dashboardLinks.dashboard.map((option) => (
              <DashboardLink
                inMediumBreakpoint={inMediumBreakpoint}
                handleMenuClickMobile={handleMenuClickMobile}
                key={option.id}
                open={open}
                option={option}
              />
            ))}
          </List>
          <Divider sx={{ my: 1.5 }} />
          <List sx={{ py: 0 }}>
            {dashboardLinks.profile.map((option) => (
              <DashboardLink
                inMediumBreakpoint={inMediumBreakpoint}
                handleMenuClickMobile={handleMenuClickMobile}
                key={option.id}
                open={open}
                option={option}
                id={account?.id}
              />
            ))}
          </List>

          <Logout showIcon />
        </Stack>
      </Stack>
      {/* <Stack>
        <Divider style={{ marginTop: '13px' }} />
        <List>
          <DashboardLink
            inMediumBreakpoint={inMediumBreakpoint}
            handleMenuClickMobile={handleMenuClickMobile}
            open={open}
            option={{
              icon: <HelpOutlineIcon width={20} height={20} />,
              text: 'Help & getting started',
              to: 'https://support.swagcharm.com/en/',
              id: 4,
              highlightIcon: true,
            }}
          />
        </List>
      </Stack> */}
    </Stack>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }} /* Better open performance on mobile. */
        sx={{ '& .MuiDrawer-paper': { overflowY: 'visible' }, display: { xs: 'block', md: 'none' } }}
      >
        {drawerMobile}
      </Drawer>

      <Drawer
        variant="permanent"
        open={open}
        onClose={() => setOpen(false)}
        sx={{ '& .MuiDrawer-paper': { overflowY: 'visible' }, display: { xs: 'none', md: 'block' } }}
      >
        {drawer}
      </Drawer>
    </>
  );
}

function DashboardLink({
  option,
  handleMenuClickMobile,
  open,
  inMediumBreakpoint,
  id,
}: {
  option: {
    id: number;
    icon: JSX.Element;
    text: string;
    to: string;
    highlightIcon?: boolean;
  };
  handleMenuClickMobile: () => void;
  open: boolean;
  inMediumBreakpoint: boolean;
  id?: number;
}) {
  const location = useLocation();

  const href = option.to;
  const isSelected = href === '/dashboard' ? href === location.pathname : location.pathname.includes(href);
  return (
    <MuiRemixLink href={href} key={option.id} underline="none" onClick={handleMenuClickMobile} prefetch="intent">
      <ListItem disablePadding sx={{ display: 'block', mb: 0.5 }}>
        <ListItemButton
          sx={{
            height: 52,
            justifyContent: !inMediumBreakpoint || open ? 'initial' : 'center',
            p: { xs: !inMediumBreakpoint || open ? '12px 16px' : '15px', md: '14px 12px' },
            borderRadius: '12px',
            bgcolor: isSelected ? 'rgba(0, 157, 200, 0.08)' : 'unset',
            color: option?.highlightIcon || isSelected ? 'primary.main' : 'text.secondary',
            '&:hover': {
              bgcolor: 'rgba(0, 157, 200, 0.08)',
              color: 'primary.main',
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: !inMediumBreakpoint || open ? 2 : 'auto',
              justifyContent: 'center',
              color: 'inherit',
              fillOpacity: 0.6,
            }}
          >
            {option.icon}
          </ListItemIcon>
          <ListItemText
            primary={
              <Typography variant="subtitle1" whiteSpace="nowrap">
                {option.text}
              </Typography>
            }
            sx={{ opacity: !inMediumBreakpoint || open ? 1 : 0 }}
          />
        </ListItemButton>
      </ListItem>
    </MuiRemixLink>
  );
}

export default function Dashboard() {
  const [open, setOpen] = React.useState(true);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [drawerWidth, setDrawerWidth] = React.useState(drawerOpen);
  const [drawerMobileWidth, setDrawerMobileWidth] = React.useState(0);
  const [openCart, setOpenCart] = React.useState(false);

  const theme = useTheme();
  const inMediumBreakpoint = useMediaQuery(theme.breakpoints.up('md'));

  const { cart }: { cart: TCart } = useMatches()[0].data;

  React.useEffect(() => {
    if (inMediumBreakpoint) {
      if (open) {
        setDrawerWidth(drawerOpen);
      } else {
        setDrawerWidth(drawerClose);
      }
    }
  }, [open, inMediumBreakpoint]);

  React.useEffect(() => {
    if (!inMediumBreakpoint) {
      if (mobileOpen) {
        setDrawerMobileWidth(drawerOpen);
      } else {
        setDrawerMobileWidth(0);
      }
    }
  }, [mobileOpen, inMediumBreakpoint]);

  return (
    <>
      <Box position="relative" sx={{ width: { md: openCart ? `calc(100vw - ${cartWidth}px)` : '100%' } }}>
        <NavbarDashboard
          cart={cart}
          drawerWidth={drawerWidth}
          setMobileOpen={setMobileOpen}
          setOpenCart={setOpenCart}
        />
        <PermanentDrawer
          drawerWidth={drawerWidth}
          drawerMobileWidth={drawerMobileWidth}
          open={open}
          setOpen={setOpen}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          inMediumBreakpoint={inMediumBreakpoint}
        />
        <Box
          bgcolor="#F0F5F9"
          // height="auto"
          height="calc(100vh - 72px)"
          overflow="auto"
          ml={{ xs: 0, md: `${drawerWidth}px` }}
          py={3}
          px={{ xs: 0, sm: 3 }}
          sx={{
            transition: theme.transitions.create('margin', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          <Outlet />
        </Box>
      </Box>
      <Cart cart={cart} open={openCart} setOpenCart={setOpenCart} />
    </>
  );
}

export function SectionContainer(props: StackProps) {
  return (
    <Stack
      bgcolor="white"
      minHeight="calc(100vh - 120px)"
      gap={{ xs: 4, sm: 3 }}
      p={{ xs: 2, sm: 3 }}
      borderRadius={{ sm: 2 }}
      {...props}
    />
  );
}

export function SectionHeader(props: StackProps) {
  return (
    <Stack
      direction={{ sm: 'row' }}
      justifyContent={{ sm: 'space-between' }}
      alignItems={{ sm: 'center' }}
      flexWrap="wrap"
      minHeight={40}
      gap={3}
      {...props}
    />
  );
}

export function SectionTitle({ title, badgeColor }: { title: string; badgeColor: string }) {
  return (
    <Stack direction="row">
      <Box minWidth={16} bgcolor={badgeColor} borderRadius={1} />
      <Typography variant="h6" ml={2}>
        {title}
      </Typography>
    </Stack>
  );
}
